import { useTranslation } from 'react-i18next';
import { formatIqd, fromMillidinars, type DrawerSessionDto } from '@gc/shared';

/**
 * A drawer session's figures, laid out as the sum they are:
 *
 *     opening float
 *   + cash taken, + incomes into the drawer, + change added from the safe
 *   − cash paid back, − expenses paid from it, − pickups, − owner takes
 *   = expected cash
 *
 * then the count, the difference, and where the counted cash went: the float
 * kept for the next session and the rest to the safe. Every figure is the one
 * stored at the close, never recomputed (owner, 2026-10-12). Shown as a sum
 * because the expected figure is what a cashier is held to, and a number they
 * cannot trace back to their own evening is one they cannot argue with or
 * agree to. Zero lines are left out so a quiet session reads as short as it
 * was; an OPEN session has stored nothing yet and shows its float alone.
 *
 * Cash only: customer-balance and FIB/Qi payments never enter the drawer.
 */
export function DrawerFigures({
  session,
  currencySymbol,
}: {
  session: DrawerSessionDto;
  currencySymbol: string;
}) {
  const { t } = useTranslation();
  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });
  const breakdown = session.breakdown;

  const lines: FigureLine[] = breakdown
    ? (
        [
          {
            key: 'cashIn',
            label: t('cashDrawer.figures.cashIn'),
            value: breakdown.cashInMd,
            sign: '+',
          },
          {
            key: 'drawerIncomes',
            label: t('cashDrawer.figures.drawerIncomes'),
            value: breakdown.drawerIncomesMd,
            sign: '+',
          },
          {
            key: 'topUps',
            label: t('cashDrawer.figures.topUps'),
            value: breakdown.topUpsMd,
            sign: '+',
          },
          {
            key: 'cashOut',
            label: t('cashDrawer.figures.cashOut'),
            value: breakdown.cashOutMd,
            sign: '−',
          },
          {
            key: 'drawerExpenses',
            label: t('cashDrawer.figures.drawerExpenses'),
            value: breakdown.drawerExpensesMd,
            sign: '−',
          },
          {
            key: 'pickups',
            label: t('cashDrawer.figures.pickups'),
            value: breakdown.pickupsMd,
            sign: '−',
          },
          {
            key: 'ownerTakes',
            label: t('cashDrawer.figures.ownerTakes'),
            value: breakdown.ownerTakesMd,
            sign: '−',
          },
        ] satisfies FigureLine[]
      ).filter((line) => BigInt(line.value) !== 0n)
    : [];

  return (
    <dl className="tabular divide-y divide-subtle rounded-card border border-subtle bg-raised text-sm">
      <Line
        label={t('cashDrawer.figures.openingFloat')}
        value={money(session.openingFloatMd)}
        data="openingFloat"
      />
      {lines.map((line) => (
        <Line key={line.key} label={line.label} value={lineValue(line, money)} data={line.key} />
      ))}
      {session.expectedCashMd != null ? (
        <Line
          label={t('cashDrawer.figures.expected')}
          value={money(session.expectedCashMd)}
          emphasis
          data="expected"
        />
      ) : null}
      {session.countedCashMd != null ? (
        <Line
          label={t('cashDrawer.figures.counted')}
          value={money(session.countedCashMd)}
          data="counted"
        />
      ) : null}
      {session.varianceMd != null ? (
        <Line
          label={t('cashDrawer.figures.variance')}
          value={signedMoney(session.varianceMd, money)}
          tone={BigInt(session.varianceMd) === 0n ? undefined : 'ending'}
          data="variance"
        />
      ) : null}
      {session.keptFloatMd != null ? (
        <Line
          label={t('cashDrawer.figures.keptFloat')}
          value={money(session.keptFloatMd)}
          data="keptFloat"
        />
      ) : null}
      {session.droppedToSafeMd != null ? (
        <Line
          label={t('cashDrawer.figures.toSafe')}
          value={money(session.droppedToSafeMd)}
          data="toSafe"
        />
      ) : null}
    </dl>
  );
}

interface FigureLine {
  key: string;
  label: string;
  value: string;
  sign: '+' | '−';
}

/**
 * A line as the sum reads it: its sign, then the amount. A line whose value
 * is itself negative flips the sign rather than printing "+ -30,000".
 */
export function lineValue(
  line: Pick<FigureLine, 'value' | 'sign'>,
  money: (value: string) => string,
): string {
  const value = BigInt(line.value);
  if (value >= 0n) return `${line.sign} ${money(line.value)}`;
  return `${line.sign === '+' ? '−' : '+'} ${money((-value).toString())}`;
}

/**
 * A difference is a signed judgement, so the positive direction carries an
 * explicit "+" — "2,000" and "+2,000" read very differently in an audit.
 */
export function signedMoney(md: string, money: (value: string) => string): string {
  return BigInt(md) > 0n ? `+${money(md)}` : money(md);
}

function Line({
  label,
  value,
  emphasis = false,
  tone,
  data,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  tone?: 'ending';
  data?: string;
}) {
  return (
    <div data-line={data} className="flex items-baseline justify-between gap-4 px-3 py-2">
      <dt className={emphasis ? 'font-medium text-content' : 'text-content-secondary'}>{label}</dt>
      <dd
        className={[
          emphasis ? 'text-base font-semibold text-content' : 'text-content',
          tone === 'ending' ? 'font-medium text-ending' : '',
        ].join(' ')}
      >
        {value}
      </dd>
    </div>
  );
}
